// recovery-trigger-classifier.ts
//
// Orchestrator recovery (CLI 3.40.0): which stopped ticket may get a
// controller-authored recovery ticket. PURE — reads only the journaled
// (kind, blocked_by, category); never `reason` text.
//
//   T1 `counted_cap`   — counted failures hit the ceiling;
//   T2 `timeout_split` — timed out `split_after_timeouts` times;
//   T3 `plan_blocked`  — a plan stopped by cap, timeout or its own `blocked`
//                        (a plan is re-planned, never split);
//   `other`            — everything else (infra, outage, governance…).
//
// The trigger ignores eligibility on purpose: a decision/survey/review ticket
// still maps to T1/T2 so the materializer journals a `not_eligible` skip.
import type { BlockedBy } from "./escalation-blocked-by.js";

export const RECOVERY_TRIGGER_VALUES = [
  "counted_cap",
  "timeout_split",
  "plan_blocked",
  "other",
] as const;

export type RecoveryTrigger = (typeof RECOVERY_TRIGGER_VALUES)[number];

export interface RecoveryTriggerInput {
  /** Ticket kind (string: a journal may name a kind this CLI does not know). */
  kind: string;
  blocked_by: BlockedBy;
  category: string;
}

export interface RecoveryTriggerVerdict {
  trigger: RecoveryTrigger;
  eligible: boolean;
  reason: "eligible" | "blocked_by_not_eligible" | "category_not_eligible" | "kind_not_eligible";
}

const PLAN_BLOCKED_BY: readonly BlockedBy[] = ["counted_cap", "worker_declared", "timeout_split"];

/** Kinds a `proposed_changes` batch can re-create (GREENING / RED_GATED adapters). */
const SPLITTABLE_KINDS: readonly string[] = ["testcase", "implement", "test", "fix"];

/** Not the ticket's content: a new ticket would hit the same wall. */
const NON_RECOVERABLE_CATEGORIES: readonly string[] = [
  "quota",
  "rate_limit",
  "auth_expired",
  "network",
  "permission",
  "contract_error",
];

export function recoveryTriggerOf(kind: string, blockedBy: BlockedBy): RecoveryTrigger {
  if (kind === "plan" && PLAN_BLOCKED_BY.includes(blockedBy)) return "plan_blocked";
  if (blockedBy === "counted_cap" || blockedBy === "timeout_split") return blockedBy;
  return "other";
}

export function classifyRecoveryTrigger(input: RecoveryTriggerInput): RecoveryTriggerVerdict {
  const trigger = recoveryTriggerOf(input.kind, input.blocked_by);
  if (trigger === "other") return { trigger, eligible: false, reason: "blocked_by_not_eligible" };
  if (NON_RECOVERABLE_CATEGORIES.includes(input.category)) {
    return { trigger, eligible: false, reason: "category_not_eligible" };
  }
  if (trigger !== "plan_blocked" && !SPLITTABLE_KINDS.includes(input.kind)) {
    return { trigger, eligible: false, reason: "kind_not_eligible" };
  }
  return { trigger, eligible: true, reason: "eligible" };
}
